/**
 * `/verify` — Cerberus email-verification landing page.
 *
 * Cerberus signup emails the user a link that points back at the
 * product app's `return_to` (e.g. `https://app.cuidari.com/verify?token=...`)
 * instead of the canonical IdP surface. This page consumes the
 * token by calling `GET /auth/verify`, shows a tiny success/error
 * panel, and bounces to `/login` so the user can finish the flow
 * with the password they just created.
 *
 * The route is public (no auth required): the user is in the gap
 * between "Cerberus account created" and "first login". `__root.tsx`
 * + `lib/cerberus/{AuthGate,redirect}` must mirror that — see
 * `/entrar` for the same contract.
 */
import { useEffect, useState, type CSSProperties } from 'react'
import {
  cerberusAuthClient,
  CerberusError,
} from '@/lib/cerberus/client'

const CTA_TEAL = '#0f766e'

const card: CSSProperties = {
  width: '100%',
  maxWidth: 420,
  borderRadius: 24,
  background: 'white',
  padding: 28,
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
  textAlign: 'center',
}

type Phase = 'verifying' | 'verified' | 'invalid' | 'expired' | 'error'

function readToken(): string | null {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('token')
}

export function VerifyPage() {
  const [phase, setPhase] = useState<Phase>('verifying')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [token] = useState<string | null>(() => readToken())

  useEffect(() => {
    if (!token) {
      setPhase('invalid')
      return
    }
    const ctrl = new AbortController()
    cerberusAuthClient
      .verifyEmail({ token }, { signal: ctrl.signal })
      .then(() => setPhase('verified'))
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return
        if (err instanceof CerberusError) {
          if (err.code === 'invalid_token' || err.status === 400) {
            setPhase('expired')
            return
          }
          setErrorMessage(err.message)
        }
        setPhase('error')
      })
    return () => ctrl.abort()
  }, [token])

  // Auto-bounce to /login on success so the user lands on the form
  // they need next (the password they just created in /entrar).
  useEffect(() => {
    if (phase !== 'verified') return
    const t = setTimeout(() => {
      if (typeof window !== 'undefined') window.location.assign('/login')
    }, 1500)
    return () => clearTimeout(t)
  }, [phase])

  return (
    <main
      data-testid="verify-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background:
          'linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 12%, var(--color-bg)) 0%, color-mix(in srgb, var(--color-accent) 8%, var(--color-bg)) 100%)',
      }}
    >
      <div style={card}>
        {phase === 'verifying' && (
          <p data-testid="verify-loading" style={{ fontSize: 14, color: 'var(--color-muted)' }}>
            Confirmando seu e-mail…
          </p>
        )}

        {phase === 'verified' && (
          <div data-testid="verify-success" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 48 }}>✅</span>
            <p style={{ fontSize: 18, fontWeight: 700 }}>E-mail confirmado!</p>
            <p style={{ fontSize: 14, color: 'var(--color-muted)' }}>
              Redirecionando para o login…
            </p>
            <a href="/login" style={{ color: CTA_TEAL, fontWeight: 600 }}>
              Ir para login
            </a>
          </div>
        )}

        {(phase === 'expired' || phase === 'invalid') && (
          <div data-testid="verify-invalid" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p role="alert" style={{ fontSize: 14, color: 'var(--color-danger)' }}>
              {phase === 'invalid'
                ? 'Link de confirmação inválido.'
                : 'Este link de confirmação expirou ou já foi usado.'}
            </p>
            <a href="/login" style={{ color: CTA_TEAL, fontWeight: 600 }}>
              Ir para login
            </a>
          </div>
        )}

        {phase === 'error' && (
          <div data-testid="verify-error" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p role="alert" style={{ fontSize: 14, color: 'var(--color-danger)' }}>
              {errorMessage ?? 'Não foi possível confirmar seu e-mail. Tente novamente em instantes.'}
            </p>
            <a href="/login" style={{ color: CTA_TEAL, fontWeight: 600 }}>
              Ir para login
            </a>
          </div>
        )}
      </div>
    </main>
  )
}
